import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { get, post, patch } from '../utils/api';
import Quiz from '../views/Quiz/Quiz';
import LessonRecap from './Sparks/LessonRecap';
import useEarnedToday, { isWorksheetRepeat, REPEAT_COPY } from './Sparks/earnedToday';
import {
  isBlankBoxCorrectInContext,
  normalizeSlotsToArray,
  getMixedSlotDisplayValue,
  isSlotCorrectInContext,
} from '../utils/quizResultsUtils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/shadcn/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn/card';
import { Button } from '@/components/shadcn/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/shadcn/accordion';
import { cn } from '@/lib/utils';

const QUESTION_COUNT_PRESETS = [5, 10, 25, 40];
const MIN_QUESTIONS = 5;
const MAX_QUESTIONS = 100;

const ResultsModal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white p-6 rounded-lg max-w-6xl w-full max-h-[80vh] overflow-y-auto max-md:mx-4 max-md:max-h-[85dvh] max-md:p-4" onClick={(e) => e.stopPropagation()}>
        <Button onClick={onClose} className="mb-4">Close</Button>
        {children}
      </div>
    </div>
  );
};

function getDisplayedAnswer(attempt) {
  if (!attempt.LessonQuestionId) return 'Question data not available';
  const qt = attempt.LessonQuestionId.questionType;
  if (qt === 'Free-Text-Question') return attempt.chosenAnswer || '';
  if (qt === 'Multiple-Choice-Question' || qt === 'Drop-Down-List') {
    const opt = attempt.LessonQuestionId.answers?.find((a) => a.id === attempt.chosenAnswer);
    return opt ? opt.text : attempt.chosenAnswer || 'Answer not found';
  }
  if (qt === 'Blank-Box-Question' && attempt.BoxTypeAnswers) {
    const allBoxes = attempt.LessonQuestionId.blankBoxes ?? [];
    return Object.entries(attempt.BoxTypeAnswers).map(([boxId, answer], i) => {
      const box = allBoxes.find((b) => b.id === boxId);
      const correct = !!box && isBlankBoxCorrectInContext(box, allBoxes, attempt.BoxTypeAnswers);
      return `Box ${i + 1}: ${answer}${box && !correct ? ` (Correct: ${box.answer})` : ''}`;
    }).join('; ');
  }
  if (qt === 'Drag-And-Drop-Question' && attempt.BoxTypeAnswers) {
    return Object.entries(attempt.BoxTypeAnswers).map(([boxId, label], i) => {
      const box = attempt.LessonQuestionId.dragBoxes?.find((b) => b.id === boxId);
      return `Box ${i + 1}: ${label}${box && label !== box.label ? ` (Correct: ${box.label})` : ''}`;
    }).join('; ');
  }
  if (qt === 'Mixed' && attempt.BoxTypeAnswers) {
    const slots = normalizeSlotsToArray(attempt.LessonQuestionId.slots);
    return slots.map((slot, i) => {
      const value = attempt.BoxTypeAnswers[slot.id] ?? '';
      const display = getMixedSlotDisplayValue(slot, value);
      const isCorrect = isSlotCorrectInContext(slot, slots, attempt.BoxTypeAnswers);
      return `Slot ${i + 1}: ${display} (${isCorrect ? 'Correct' : 'Incorrect'})`;
    }).join('; ');
  }
  return attempt.chosenAnswer || '—';
}

function getExplanationDisplayedAnswer(attempt) {
  const eq = attempt.explanationQuestionId;
  if (!eq || typeof eq !== 'object') {
    return attempt.explanationChosenAnswer || 'No answer provided';
  }
  const qt = eq.questionType;

  // Box-based types store answers per slot/box id in explanationBoxTypeAnswers,
  // where dropdown/multiple-choice slots hold option ids that must be resolved to text.
  if (qt === 'Mixed' && attempt.explanationBoxTypeAnswers) {
    const slots = normalizeSlotsToArray(eq.slots);
    return slots.map((slot, i) => {
      const value = attempt.explanationBoxTypeAnswers[slot.id] ?? '';
      const display = getMixedSlotDisplayValue(slot, value);
      const isCorrect = isSlotCorrectInContext(slot, slots, attempt.explanationBoxTypeAnswers);
      return `Slot ${i + 1}: ${display} (${isCorrect ? 'Correct' : 'Incorrect'})`;
    }).join('; ');
  }
  if (qt === 'Blank-Box-Question' && attempt.explanationBoxTypeAnswers) {
    const allBoxes = eq.blankBoxes ?? [];
    return Object.entries(attempt.explanationBoxTypeAnswers).map(([boxId, answer], i) => {
      const box = allBoxes.find((b) => b.id === boxId);
      const correct = !!box && isBlankBoxCorrectInContext(box, allBoxes, attempt.explanationBoxTypeAnswers);
      return `Box ${i + 1}: ${answer}${box && !correct ? ` (Correct: ${box.answer})` : ''}`;
    }).join('; ');
  }
  if (qt === 'Drag-And-Drop-Question' && attempt.explanationBoxTypeAnswers) {
    const allBoxes = eq.dragBoxes ?? [];
    return Object.entries(attempt.explanationBoxTypeAnswers).map(([boxId, label], i) => {
      const box = allBoxes.find((b) => b.id === boxId);
      return `Box ${i + 1}: ${label}${box && label !== box.label ? ` (Correct: ${box.label})` : ''}`;
    }).join('; ');
  }

  if (!attempt.explanationChosenAnswer) return 'No answer provided';
  if (qt === 'Free-Text-Question') return attempt.explanationChosenAnswer;
  if (qt === 'Multiple-Choice-Question' || qt === 'Drop-Down-List') {
    const opt = eq.answers?.find((a) => a.id === attempt.explanationChosenAnswer);
    return opt ? opt.text : attempt.explanationChosenAnswer;
  }
  return attempt.explanationChosenAnswer;
}

function WorksheetResultsContent({ submission }) {
  const attempts = submission.questionAttempts || [];
  const correctCount = attempts.filter((a) => a.QuestionStatus === 'correct').length;
  const totalCount = attempts.length;
  const scorePercent = totalCount > 0 ? Math.round((correctCount / totalCount) * 100) : 0;

  return (
    <div>
      <h2 className="text-2xl font-bold mb-4">
        {submission.name}
        {submission.timerEnabled && (
          <span className="ml-2 px-2 py-1 text-sm rounded-full bg-amber-100 text-amber-800">Timed</span>
        )}
      </h2>
      <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-blue-800">Final Score</h3>
          <div className="text-right">
            <div className="text-2xl font-bold text-blue-900">{correctCount}/{totalCount}</div>
            <div className="text-sm text-blue-700">({scorePercent}%)</div>
          </div>
        </div>
        <div className="text-sm text-blue-600 mt-2">
          Completed: {(submission.completionDate || submission.createdAt)
            ? new Date(submission.completionDate || submission.createdAt).toLocaleString()
            : '—'}
        </div>
      </div>
      <div>
        <h3 className="text-lg font-semibold mb-3">Question Details</h3>
        {attempts.map((attempt, index) => (
          <div key={index} className="mb-8">
            <div className="flex gap-4 flex-wrap">
              <div className={`flex-1 min-w-[280px] p-4 border rounded ${
                attempt.QuestionStatus === 'correct' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
              }`}>
                <p className="font-bold mb-2">Question {index + 1}</p>
                {attempt.LessonQuestionId ? (
                  <>
                    <p className="mb-2"><strong>Question:</strong> {attempt.LessonQuestionId.question}</p>
                    {attempt.LessonQuestionId.image && (
                      <div className="mb-2">
                        <img src={attempt.LessonQuestionId.image} alt="Question" className="max-h-[180px] max-w-full object-contain border rounded" />
                      </div>
                    )}
                    {!(attempt.LessonQuestionId.questionType === 'Blank-Box-Question' || attempt.LessonQuestionId.questionType === 'Drag-And-Drop-Question') && (
                      <p className="mb-2"><strong>Student answer:</strong> {getDisplayedAnswer(attempt)}</p>
                    )}
                    <p className="font-medium">
                      <strong>Result:</strong>{' '}
                      {attempt.QuestionStatus === 'correct' ? (
                        <span className="text-green-600">Correct</span>
                      ) : (
                        <span className="text-red-600">Incorrect</span>
                      )}
                    </p>
                  </>
                ) : (
                  <p className="text-red-600">Question data not available</p>
                )}
              </div>
              {attempt.explanationQuestionId && (
                <div className={`flex-1 min-w-[280px] p-4 border rounded ${
                  !attempt.explanationResponseStatus ? 'bg-gray-100' : attempt.explanationResponseStatus === 'correct' ? 'bg-green-100 border-green-200' : 'bg-red-100 border-red-200'
                }`}>
                  <p className="font-bold mb-2">Explanation question</p>
                  {typeof attempt.explanationQuestionId === 'object' && attempt.explanationQuestionId ? (
                    <>
                      <p className="mb-2"><strong>Question:</strong> {attempt.explanationQuestionId.question}</p>
                      {attempt.explanationResponseStatus && (
                        <p className="mb-2"><strong>Answer:</strong> {getExplanationDisplayedAnswer(attempt)}</p>
                      )}
                      <p className="font-medium">
                        <strong>Result:</strong>{' '}
                        {attempt.explanationResponseStatus === 'correct' ? 'Correct' : attempt.explanationResponseStatus === 'incorrect' ? 'Incorrect' : 'Not attempted'}
                      </p>
                    </>
                  ) : (
                    <p className="text-gray-600">Explanation data not available</p>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Quiz Studio: create worksheets, view pending and completed.
 * Nav shows "Quiz Studio"; within the page: Create, Pending, Completed tabs.
 */
function Worksheets() {
  const { loggedInChild } = useAuth();
  const [activeTab, setActiveTab] = useState('create');

  // Create form state
  const [topics, setTopics] = useState([]);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [selectedLessonIds, setSelectedLessonIds] = useState([]);
  const [questionCountPreset, setQuestionCountPreset] = useState(10);
  const [useCustomCount, setUseCustomCount] = useState(false);
  const [customCountInput, setCustomCountInput] = useState('');
  const [timerEnabled, setTimerEnabled] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [errors, setErrors] = useState([]);
  const [generateSuccess, setGenerateSuccess] = useState(null);

  const [pendingList, setPendingList] = useState([]);
  // Refetched whenever the pending list changes, so a worksheet's chip follows what the child has done today.
  const earnedToday = useEarnedToday(loggedInChild?._id, pendingList);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [activeWorksheetId, setActiveWorksheetId] = useState(null);
  const [showRecap, setShowRecap] = useState(false);
  const [recapRepeat, setRecapRepeat] = useState(false);

  const [completedList, setCompletedList] = useState([]);
  const [completedLoading, setCompletedLoading] = useState(false);

  const [expandedWorksheetId, setExpandedWorksheetId] = useState(null);
  const [worksheetDetailsCache, setWorksheetDetailsCache] = useState({});
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const [resultsModalSubmission, setResultsModalSubmission] = useState(null);
  const [resultsModalLoading, setResultsModalLoading] = useState(false);

  useEffect(() => {
    if (loggedInChild?._id) {
      setLessonsLoading(true);
      get(`/worksheets/lessons-for-child/${loggedInChild._id}`, true)
        .then((data) => {
          setTopics(Array.isArray(data) ? data : []);
        })
        .catch(() => setTopics([]))
        .finally(() => setLessonsLoading(false));
    } else {
      setTopics([]);
    }
  }, [loggedInChild?._id]);

  useEffect(() => {
    if (activeTab === 'pending' && loggedInChild?._id) {
      setPendingLoading(true);
      get(`/worksheets/pending/${loggedInChild._id}`, true)
        .then((data) => setPendingList(Array.isArray(data) ? data : []))
        .catch(() => setPendingList([]))
        .finally(() => setPendingLoading(false));
    }
  }, [activeTab, loggedInChild?._id]);

  useEffect(() => {
    if (activeTab === 'completed' && loggedInChild?._id) {
      setCompletedLoading(true);
      get(`/worksheets/completed/${loggedInChild._id}`, true)
        .then((data) => setCompletedList(Array.isArray(data) ? data : []))
        .catch(() => setCompletedList([]))
        .finally(() => setCompletedLoading(false));
    }
  }, [activeTab, loggedInChild?._id]);

  const fetchWorksheetDetails = (id) => {
    if (worksheetDetailsCache[id]) return;
    get(`/worksheets/submission/${id}`, true)
      .then((data) => {
        const lessons =
          Array.isArray(data.lessonTitles) && data.lessonTitles.length > 0
            ? data.lessonTitles
            : (data.selectedLessonIds || []).map((l) =>
                typeof l === 'object' && l != null && l.title != null ? l.title : (typeof l === 'string' ? l : 'Unknown lesson')
              );
        setWorksheetDetailsCache((prev) => ({ ...prev, [id]: { lessons } }));
      })
      .catch(() => {});
  };

  const handleExpandName = (id) => {
    if (renamingId) return;
    setExpandedWorksheetId((prev) => (prev === id ? null : id));
    if (expandedWorksheetId !== id) fetchWorksheetDetails(id);
  };

  const handleStartRename = (w) => {
    setRenamingId(w._id);
    setRenameValue(w.name || '');
  };

  const handleSaveRename = async (id) => {
    if (renamingId !== id) return;
    const value = renameValue.trim();
    setRenamingId(null);
    setRenameValue('');
    if (!value) return;
    try {
      await patch(`/worksheets/${id}`, { name: value }, true);
      setPendingList((prev) => prev.map((item) => (item._id === id ? { ...item, name: value } : item)));
      setCompletedList((prev) => prev.map((item) => (item._id === id ? { ...item, name: value } : item)));
      if (resultsModalSubmission?._id === id) setResultsModalSubmission((s) => (s ? { ...s, name: value } : null));
    } catch (err) {
      console.error('Rename failed:', err);
    }
  };

  const handleViewResults = (id) => {
    setResultsModalLoading(true);
    setResultsModalSubmission(null);
    get(`/worksheets/submission/${id}`, true)
      .then((data) => setResultsModalSubmission(data))
      .catch(() => setResultsModalSubmission(null))
      .finally(() => setResultsModalLoading(false));
  };

  const allLessonIds = topics.flatMap((t) => (t.lessons || []).map((l) => l.lessonId));

  const toggleLesson = (lessonId) => {
    setSelectedLessonIds((prev) =>
      prev.includes(lessonId) ? prev.filter((id) => id !== lessonId) : [...prev, lessonId]
    );
    setErrors((e) => e.filter((x) => x !== 'Select at least one lesson'));
  };

  const selectAllLessons = () => {
    setSelectedLessonIds([...allLessonIds]);
    setErrors((e) => e.filter((x) => x !== 'Select at least one lesson'));
  };

  const clearAllLessons = () => {
    setSelectedLessonIds([]);
  };

  const getEffectiveQuestionCount = () => {
    if (useCustomCount) {
      const n = parseInt(customCountInput, 10);
      if (Number.isNaN(n)) return null;
      return n;
    }
    return questionCountPreset;
  };

  const validate = () => {
    const errs = [];
    if (selectedLessonIds.length === 0) errs.push('Select at least one lesson.');
    const count = getEffectiveQuestionCount();
    if (count == null) errs.push('Enter a valid number of questions (5–100).');
    else if (count < MIN_QUESTIONS || count > MAX_QUESTIONS)
      errs.push(`Number of questions must be between ${MIN_QUESTIONS} and ${MAX_QUESTIONS}.`);
    setErrors(errs);
    return errs.length === 0;
  };

  const handleGenerate = async () => {
    setGenerateSuccess(null);
    if (!validate()) return;
    const count = getEffectiveQuestionCount();
    if (count == null || count < MIN_QUESTIONS || count > MAX_QUESTIONS) return;
    if (!loggedInChild?._id) {
      setErrors(['Please select a child first.']);
      return;
    }
    setGenerating(true);
    setErrors([]);
    try {
      await post(
        '/worksheets/generate',
        {
          childId: loggedInChild._id,
          lessonIds: selectedLessonIds,
          requestedQuestionCount: count,
          timerEnabled,
        },
        true
      );
      setGenerateSuccess('Worksheet created. Check the Pending tab to start it.');
      setActiveTab('pending');
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Failed to create worksheet.';
      setErrors([message]);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto">
        {showRecap ? (
          <LessonRecap continueLabel="Back to Quiz Studio" onContinue={() => setShowRecap(false)} detail={recapRepeat ? REPEAT_COPY.worksheetFinish : undefined} showShop>
            You&apos;ve finished the worksheet!
          </LessonRecap>
        ) : activeWorksheetId ? (
          <div>
            <div className="mb-4">
              <Button
                variant="outline"
                onClick={() => setActiveWorksheetId(null)}
                className="bg-gray-600 hover:bg-gray-700 text-white border-0"
              >
                ← Back to Quiz Studio
              </Button>
            </div>
            <Quiz
              isWorksheet={true}
              worksheetSubmissionId={activeWorksheetId}
              preventNavigation={true}
              onQuizComplete={() => {
                setRecapRepeat(isWorksheetRepeat(earnedToday, activeWorksheetId));
                setShowRecap(true);
                setActiveWorksheetId(null);
                setPendingList((prev) => prev.filter((w) => w._id !== activeWorksheetId));
                setActiveTab('completed');
              }}
            />
          </div>
        ) : !loggedInChild ? (
          <div className="text-center py-8">
            <Card className="max-w-md mx-auto border-blue-200 bg-blue-50">
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-blue-800 font-medium">
                    Please select a child in the <strong>Children Dashboard</strong> to create and view worksheets.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
        <>
        <h1 className="text-3xl font-bold text-gray-900 mb-6">
          Quiz Studio
        </h1>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="create">Create</TabsTrigger>
            <TabsTrigger value="pending">Pending</TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
          </TabsList>

          <TabsContent value="create" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Create worksheet</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {generateSuccess && (
                  <p className="text-sm text-green-600 bg-green-50 p-3 rounded">{generateSuccess}</p>
                )}
                {errors.length > 0 && (
                  <ul className="text-sm text-red-600 bg-red-50 p-3 rounded list-disc list-inside">
                    {errors.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                )}

                {/* Lesson picker */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">Lessons (by topic)</label>
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={selectAllLessons}>
                        Select all
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={clearAllLessons}>
                        Clear all
                      </Button>
                    </div>
                  </div>
                  {lessonsLoading ? (
                    <p className="text-gray-500 text-sm">Loading lessons…</p>
                  ) : topics.length === 0 ? (
                    <p className="text-gray-500 text-sm">No lessons with questions available for this year group.</p>
                  ) : (
                    <Accordion type="multiple" className="w-full border rounded-md p-2">
                      {topics.map((topic) => (
                        <AccordionItem key={topic.topicId} value={topic.topicId}>
                          <AccordionTrigger className="text-left">
                            {topic.topicName === 'View Lesson' ? 'Topics' : topic.topicName}
                          </AccordionTrigger>
                          <AccordionContent>
                            <ul className="space-y-2 pl-2">
                              {(topic.lessons || []).map((lesson) => (
                                <li key={lesson.lessonId} className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    id={`lesson-${lesson.lessonId}`}
                                    checked={selectedLessonIds.includes(lesson.lessonId)}
                                    onChange={() => toggleLesson(lesson.lessonId)}
                                    className="h-4 w-4 rounded border-gray-300"
                                  />
                                  <label htmlFor={`lesson-${lesson.lessonId}`} className="text-sm cursor-pointer flex-1">
                                    {lesson.title}
                                  </label>
                                </li>
                              ))}
                            </ul>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  )}
                </div>

                {/* Question count */}
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Number of questions</label>
                  <div className="flex flex-wrap gap-2 items-center">
                    {QUESTION_COUNT_PRESETS.map((n) => (
                      <Button
                        key={n}
                        type="button"
                        variant={!useCustomCount && questionCountPreset === n ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => {
                          setUseCustomCount(false);
                          setQuestionCountPreset(n);
                        }}
                      >
                        {n}
                      </Button>
                    ))}
                    <span className="text-sm text-gray-500">or</span>
                    <input
                      type="number"
                      min={MIN_QUESTIONS}
                      max={MAX_QUESTIONS}
                      placeholder="Other (5–100)"
                      value={useCustomCount ? customCountInput : ''}
                      onChange={(e) => {
                        setCustomCountInput(e.target.value);
                        setUseCustomCount(true);
                      }}
                      onFocus={() => setUseCustomCount(true)}
                      className={cn(
                        'min-w-[7.5rem] w-36 px-2 py-1.5 text-sm border rounded-md',
                        useCustomCount && 'ring-2 ring-primary'
                      )}
                    />
                  </div>
                </div>

                {/* Timer */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="timer-enabled"
                    checked={timerEnabled}
                    onChange={(e) => setTimerEnabled(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <label htmlFor="timer-enabled" className="text-sm font-medium text-gray-700 cursor-pointer">
                    Set a timer
                  </label>
                </div>

                <Button onClick={handleGenerate} disabled={generating || !loggedInChild}>
                  {generating ? 'Creating…' : 'Generate worksheet'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pending" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Pending worksheets</CardTitle>
              </CardHeader>
              <CardContent>
                {!loggedInChild && (
                  <p className="text-gray-600 text-sm mb-4">Select a child to see pending worksheets.</p>
                )}
                {pendingLoading ? (
                  <p className="text-gray-500 text-sm">Loading…</p>
                ) : pendingList.length === 0 ? (
                  <p className="text-gray-600 text-sm">No pending worksheets. Create one from the Create tab.</p>
                ) : (
                  <ul className="space-y-2">
                    {pendingList.map((w) => (
                      <li
                        key={w._id}
                        className={cn(
                          'rounded-lg border-2 transition-colors',
                          expandedWorksheetId === w._id ? 'border-teal-300 bg-teal-50/50' : 'border-gray-200 bg-gray-50/50 hover:border-gray-300 hover:bg-gray-50'
                        )}
                      >
                        <div
                          role="button"
                          tabIndex={0}
                          className="p-3 flex items-center justify-between gap-2 flex-wrap cursor-pointer"
                          onClick={() => handleExpandName(w._id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleExpandName(w._id);
                            }
                          }}
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            {renamingId === w._id ? (
                              <input
                                type="text"
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                onBlur={() => handleSaveRename(w._id)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveRename(w._id);
                                  if (e.key === 'Escape') {
                                    setRenamingId(null);
                                    setRenameValue('');
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="text-sm border rounded px-2 py-1 flex-1 max-w-[200px]"
                                autoFocus
                              />
                            ) : (
                              <button
                                type="button"
                                onDoubleClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleStartRename(w);
                                }}
                                className="text-left text-sm font-medium hover:underline cursor-pointer"
                              >
                                {w.name}
                                <span className="ml-1 text-gray-400 text-xs">
                                  {expandedWorksheetId === w._id ? '▼ click to close' : '▶ click to expand'}
                                </span>
                              </button>
                            )}
                            {w.timerEnabled && (
                              <span className="text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded shrink-0" title="Timed worksheet">
                                Timed
                              </span>
                            )}
                            {isWorksheetRepeat(earnedToday, w._id) && (
                              <span className="text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded shrink-0" title={REPEAT_COPY.worksheetStart} data-testid="worksheet-repeat">
                                {REPEAT_COPY.worksheetChip}
                              </span>
                            )}
                          </div>
                          <span className="text-gray-500 text-sm shrink-0">{w.actualQuestionCount} questions</span>
                          <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                            {renamingId !== w._id && (
                              <Button type="button" variant="outline" size="sm" onClick={() => handleStartRename(w)}>
                                Rename
                              </Button>
                            )}
                            <Button size="sm" onClick={() => setActiveWorksheetId(w._id)}>
                              Start
                            </Button>
                          </div>
                        </div>
                        {expandedWorksheetId === w._id && (
                          <div className="px-3 pb-3 pt-0">
                            <p className="text-sm font-medium text-gray-700 mb-1">Lessons included:</p>
                            {worksheetDetailsCache[w._id]?.lessons?.length ? (
                              <ul className="list-disc list-inside text-sm text-gray-600 space-y-0.5">
                                {worksheetDetailsCache[w._id].lessons.map((lessonName, idx) => (
                                  <li key={idx}>{lessonName}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-sm text-gray-500">Loading…</p>
                            )}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="completed" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Completed worksheets</CardTitle>
              </CardHeader>
              <CardContent>
                {!loggedInChild && (
                  <p className="text-gray-600 text-sm mb-4">Select a child to see completed worksheets.</p>
                )}
                {completedLoading ? (
                  <p className="text-gray-500 text-sm">Loading…</p>
                ) : completedList.length === 0 ? (
                  <p className="text-gray-600 text-sm">No completed worksheets yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {completedList.map((w) => (
                      <li
                        key={w._id}
                        className={cn(
                          'rounded-lg border-2 transition-colors',
                          expandedWorksheetId === w._id ? 'border-teal-300 bg-teal-50/50' : 'border-gray-200 bg-gray-50/50 hover:border-gray-300 hover:bg-gray-50'
                        )}
                      >
                        <div
                          role="button"
                          tabIndex={0}
                          className="p-3 flex items-center justify-between gap-2 flex-wrap cursor-pointer"
                          onClick={() => handleExpandName(w._id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleExpandName(w._id);
                            }
                          }}
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            {renamingId === w._id ? (
                              <input
                                type="text"
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                onBlur={() => handleSaveRename(w._id)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveRename(w._id);
                                  if (e.key === 'Escape') {
                                    setRenamingId(null);
                                    setRenameValue('');
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="text-sm border rounded px-2 py-1 flex-1 max-w-[200px]"
                                autoFocus
                              />
                            ) : (
                              <button
                                type="button"
                                onDoubleClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleStartRename(w);
                                }}
                                className="text-left text-sm font-medium hover:underline cursor-pointer"
                              >
                                {w.name}
                                <span className="ml-1 text-gray-400 text-xs">
                                  {expandedWorksheetId === w._id ? '▼ click to close' : '▶ click to expand'}
                                </span>
                              </button>
                            )}
                            {w.timerEnabled && (
                              <span className="text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded shrink-0" title="Timed worksheet">
                                Timed
                              </span>
                            )}
                          </div>
                          <span className="text-gray-500 text-sm shrink-0">
                            {w.correctCount != null && w.totalCount != null
                              ? `${w.correctCount}/${w.totalCount} (${w.scorePercent ?? 0}%)`
                              : `${w.actualQuestionCount} questions`}
                          </span>
                          <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                            {renamingId !== w._id && (
                              <Button type="button" variant="outline" size="sm" onClick={() => handleStartRename(w)}>
                                Rename
                              </Button>
                            )}
                            <Button size="sm" variant="secondary" onClick={() => handleViewResults(w._id)}>
                              View results
                            </Button>
                          </div>
                        </div>
                        {expandedWorksheetId === w._id && (
                          <div className="px-3 pb-3 pt-0">
                            <p className="text-sm font-medium text-gray-700 mb-1">Lessons included:</p>
                            {worksheetDetailsCache[w._id]?.lessons?.length ? (
                              <ul className="list-disc list-inside text-sm text-gray-600 space-y-0.5">
                                {worksheetDetailsCache[w._id].lessons.map((lessonName, idx) => (
                                  <li key={idx}>{lessonName}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-sm text-gray-500">Loading…</p>
                            )}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        </>
        )}

        <ResultsModal
          isOpen={resultsModalSubmission != null || resultsModalLoading}
          onClose={() => {
            if (!resultsModalLoading) setResultsModalSubmission(null);
          }}
        >
          {resultsModalLoading ? (
            <p className="text-gray-500">Loading results…</p>
          ) : resultsModalSubmission ? (
            <WorksheetResultsContent submission={resultsModalSubmission} />
          ) : null}
        </ResultsModal>
      </div>
    </div>
  );
}

export default Worksheets;
